import { createSlice } from '@reduxjs/toolkit';
import { statusTypes } from '@/constants/types';
import {
  fetchCampaignProducts,
  fetchInfinityCampaignProducts,
  getCampaignFilterKey,
} from '../thunk/campaign-products-thunk';

// Initial state
const initialState = {
  currentCampaign: null,
  data: [],
  currentCampaignBanner: {},
  filterKey: [],
  selectedFilterItems: null,
  selectedColors: [],
  filterParamObjects: '',
  isLoading: false,
  total: 0,
  page: 1,
  nextPageUrl: null,
  limit: 12,
  slug: '',
  fetchStatus: statusTypes.idle,
  sectionGroupItems: [],
  sortingFilter: '',
};

// Slice
const campaignProductsSlice = createSlice({
  name: 'campaignProducts',
  initialState,
  reducers: {
    reset: (state) => initialState,
    resetAppliedFilters: (state) => {
      state.selectedFilterItems = null;
      state.selectedColors = [];
      state.filterParamObjects = '';
      state.page = 1;
    },
    setCampaignSelectedFilterItems: (state, action) => {
      state.selectedFilterItems = action.payload;
      state.page = 1;
    },
    setCampaignSelectedColors: (state, action) => {
      state.selectedColors = action.payload;
      state.page = 1;
    },
    resetCampaignProductFilters: (state) => {
      state.selectedColors = [];
      state.selectedFilterItems = null;
      state.filterParamObjects = '';
      state.page = 1;
    },
    resetCurrentCampaignBanner: (state) => {
      state.currentCampaignBanner = {};
    },
    setCampaignFilterParamObjects: (state, action) => {
      state.filterParamObjects = action.payload;
    },
    setCampaignSlug: (state, action) => {
      state.slug = action.payload;
    },
    setCurrentCampaign: (state, action) => {
      state.currentCampaign = action.payload;
    },
    updateCampaignFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
      state.page = 1;
      state.data = [];
      state.limit = 12;
      state.nextPageUrl = null;
    },
    setSortingFilters: (state, action) => {
      state.sortingFilter = action.payload;
      state.page = 1;
    },
    setSectionGroupItems: (state, action) => {
      const newItems = action.payload;
      const uniqueItems = newItems.filter(
        (newItem) =>
          !state.sectionGroupItems.some((item) => item.id === newItem.id),
      );
      state.sectionGroupItems = [...state.sectionGroupItems, ...uniqueItems];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCampaignProducts.pending, (state) => {
        state.isLoading = true;
        state.total = 0; // Uncommented this line
        state.error = null;
        state.fetchStatus = statusTypes.loading;
      })
      .addCase(fetchCampaignProducts.fulfilled, (state, action) => {
        const responseData = action.payload;
        if (!responseData) {
          state.data = [];
          state.page = 1;
          state.limit = 12;
          state.nextPageUrl = null;
          state.total = 0;
          state.isLoading = false;
          state.fetchStatus = statusTypes.succeeded;
          state.currentCampaignBanner = {};
        } else {
          state.currentCampaignBanner = action?.payload?.campaign ?? {};
          const currentPage = responseData?.pagination?.current_page ?? 0;
          state.fetchStatus = statusTypes.succeeded;
          state.data = responseData.items;
          state.page = currentPage + 1;
          state.nextPageUrl = responseData?.pagination?.next ?? null;
          state.total = responseData?.pagination?.total ?? 0;
          state.limit = 12;
        }
        state.isLoading = false;
      })

      .addCase(fetchCampaignProducts.rejected, (state, action) => {
        state.isLoading = false;
        state.nextPageUrl = null;
        state.error = action.payload;
        state.fetchStatus = statusTypes.failed;
      })

      .addCase(fetchInfinityCampaignProducts.pending, (state) => {
        state.isLoading = true; // Added isLoading update
        state.error = null;
        state.fetchStatus = statusTypes.fetching;
      })
      .addCase(fetchInfinityCampaignProducts.fulfilled, (state, action) => {
        const responseData = action.payload;
        if (!responseData) {
          state.data = [];
          state.page = 1;
          state.limit = 12;
          state.nextPageUrl = null;
          state.total = 0;
          state.fetchStatus = statusTypes.succeeded;
        } else {
          const currentPage = responseData?.pagination?.current_page ?? 0;
          state.fetchStatus = statusTypes.succeeded;
          state.data =
            state.page === 1
              ? responseData.items
              : [...state.data, ...responseData.items];
          state.page = currentPage + 1;
          state.nextPageUrl = responseData?.pagination?.next ?? null;
          state.total = responseData?.pagination?.total ?? 0;
          state.limit = 12;
        }
      })
      .addCase(fetchInfinityCampaignProducts.rejected, (state, action) => {
        state.isLoading = false;
        state.nextPageUrl = null;
        state.fetchStatus = statusTypes.failed;
        state.error = action.payload;
      })

      .addCase(getCampaignFilterKey.pending, (state) => {
        // state.isLoading = true;
        state.error = null;
        state.filterKey = [];
        state.filterParamObjects = '';
      })
      .addCase(getCampaignFilterKey.fulfilled, (state, action) => {
        // state.isLoading = false;
        state.filterKey = action.payload;
      })
      .addCase(getCampaignFilterKey.rejected, (state, action) => {
        // state.isLoading = false;
        state.error = action.payload;
        state.filterKey = [];
      });
  },
});

export const {
  reset,
  setCampaignSlug,
  resetAppliedFilters,
  setCurrentCampaign,
  resetCampaignProductFilters,
  resetCurrentCampaignBanner,
  setCampaignSelectedFilterItems,
  setCampaignSelectedColors,
  setCampaignFilterParamObjects,
  updateCampaignFilters,
  setSectionGroupItems,
  setSortingFilters,
} = campaignProductsSlice.actions;

export default campaignProductsSlice.reducer;
